import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Activity, Smartphone, Palette, Play, Check, X } from "lucide-react";
import { MICRO_ACTION_CATEGORIES } from "../data/microActions";
import { api } from "../lib/api";
import { toast } from "sonner";

const ICON_MAP = { Sparkles, Activity, Smartphone, Palette };

const CATEGORY_BG = {
  declutter: "from-[#F5D0D6]/40 to-[#FDFBF7]",
  movement: "from-[#234B38]/10 to-[#FDFBF7]",
  digital_detox: "from-[#E2EBE6] to-[#FDFBF7]",
  creativity: "from-[#F5D0D6]/30 to-[#E2EBE6]/40",
};

const MicroAction = ({ action }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const categoryMeta = MICRO_ACTION_CATEGORIES.find((c) => c.id === action.category) || MICRO_ACTION_CATEGORIES[0];
  const Icon = ICON_MAP[categoryMeta.icon] || Sparkles;
  const bg = CATEGORY_BG[action.category] || "from-[#FDFBF7] to-white";
  const today = new Date().toISOString().slice(0, 10);

  // Read today's status for this action from backend logs (so refresh shows persisted state).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.listMicroActionLogs();
        if (!alive) return;
        const existing = (r.data.items || []).find(
          (l) => l.action_id === action.id && l.date === today
        );
        if (existing) setStatus(existing.status);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [action.id, today]);

  const setBackendStatus = async (next) => {
    setBusy(true);
    try {
      await api.logMicroAction({
        action_id: action.id,
        category: action.category,
        title: action.title,
        status: next,
      });
      setStatus(next);
      toast.success(next === "completed" ? "Marked done" : next === "started" ? "Started" : "Skipped");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="px-6 pb-6" data-testid="micro-action-section">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">
          Today's Micro Action
        </p>
        <button
          onClick={() => navigate("/micro-actions")}
          data-testid="micro-action-browse-btn"
          className="text-[10px] uppercase tracking-wider font-semibold text-[#5C7568] hover:text-[#234B38]"
        >
          Browse all
        </button>
      </div>
      <div
        className={`rounded-3xl bg-gradient-to-br ${bg} border border-[#E2EBE6] p-6 shadow-[0_8px_32px_rgba(35,75,56,0.06)]`}
        data-testid="micro-action-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white border border-[#E2EBE6] flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[#234B38]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#234B38]" data-testid="micro-action-type">
                {categoryMeta.label}
              </p>
              {status === "completed" && (
                <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#234B38] text-white rounded-full px-2 py-0.5">
                  Done
                </span>
              )}
              {status === "started" && (
                <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#F5D0D6] text-[#D6336C] rounded-full px-2 py-0.5">
                  Started
                </span>
              )}
              {status === "skipped" && (
                <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#E2EBE6] text-[#5C7568] rounded-full px-2 py-0.5">
                  Skipped
                </span>
              )}
            </div>
            <h3 className="text-lg font-medium text-[#1A2E24] mt-1 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="micro-action-title">
              {action.title}
            </h3>
            <p className="text-sm text-[#5C7568] leading-relaxed mt-2">{action.body}</p>
          </div>
        </div>

        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            data-testid="micro-action-try-btn"
            className="mt-5 inline-flex items-center gap-1.5 text-[#234B38] text-xs font-semibold uppercase tracking-wider hover:gap-2 transition-all"
          >
            <span>{status ? "Update status" : "Try it now"}</span>
            <Play className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        ) : (
          <div className="mt-5 flex gap-2" data-testid="micro-action-actions">
            <button
              onClick={() => setBackendStatus("started")}
              disabled={busy}
              data-testid="micro-action-start-btn"
              className={`flex-1 rounded-full py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                status === "started"
                  ? "bg-[#F5D0D6] text-[#D6336C]"
                  : "bg-white border border-[#E2EBE6] text-[#5C7568] hover:bg-[#F5D0D6]/30"
              }`}
            >
              <Play className="w-3.5 h-3.5" /> Start
            </button>
            <button
              onClick={() => setBackendStatus("completed")}
              disabled={busy}
              data-testid="micro-action-complete-btn"
              className={`flex-1 rounded-full py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                status === "completed"
                  ? "bg-[#234B38] text-white"
                  : "bg-white border border-[#E2EBE6] text-[#5C7568] hover:bg-[#234B38] hover:text-white"
              }`}
            >
              <Check className="w-3.5 h-3.5" /> Complete
            </button>
            <button
              onClick={() => setBackendStatus("skipped")}
              disabled={busy}
              data-testid="micro-action-skip-btn"
              className={`flex-1 rounded-full py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                status === "skipped"
                  ? "bg-[#E2EBE6] text-[#5C7568]"
                  : "bg-white border border-[#E2EBE6] text-[#5C7568] hover:bg-[#E2EBE6]"
              }`}
            >
              <X className="w-3.5 h-3.5" /> Skip
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MicroAction;
